import {
    GET_ADMIN_CLIENT_REQUESTS,
    SET_ADMIN_CLIENT_REQUESTS,
    GET_ADMIN_CLIENT_REQUEST,
    SET_ADMIN_CLIENT_REQUEST,
    STORE_ADMIN_CLIENT_REQUEST,
    ADD_ADMIN_CLIENT_REQUEST,
    UPDATE_ADMIN_CLIENT_REQUEST,
    UPDATE_ADMIN_CLIENT_REQUEST_STATUS,
    DELETE_ADMIN_CLIENT_REQUEST,
    REMOVE_ADMIN_CLIENT_REQUEST,
    STORE_ADMIN_CLIENT_REQUEST_CLIENT,
    ADD_ADMIN_CLIENT_REQUEST_CLIENT,
    UPDATE_ADMIN_CLIENT_REQUEST_CLIENT,
    SET_ADMIN_CLIENT_REQUEST_CLIENT,
    DELETE_ADMIN_CLIENT_REQUEST_CLIENT,
    REMOVE_ADMIN_CLIENT_REQUEST_CLIENT,
    STORE_ADMIN_CLIENT_REQUEST_COMMENT,
    ADD_ADMIN_CLIENT_REQUEST_COMMENT,
    DELETE_ADMIN_CLIENT_REQUEST_COMMENT,
    REMOVE_ADMIN_CLIENT_REQUEST_COMMENT,
    GET_ADMIN_CLIENT_REQUEST_OPTIONS,
    SET_ADMIN_CLIENT_REQUEST_OPTIONS,
    STORE_ADMIN_CLIENT_REQUEST_FILE,
    ADD_ADMIN_CLIENT_REQUEST_FILE,
    DELETE_ADMIN_CLIENT_REQUEST_FILE,
    REMOVE_ADMIN_CLIENT_REQUEST_FILE,
    SET_ADMIN_CLIENT_REQUEST_LIST_STATE
} from './actions';
import AdminClientRequestService from '@services/admin_client_request.service';

const defaultState = () => {
    return {
        clientRequests: {},
        clientRequest: null,
        clientRequestOptions: {
            kindergartens: [],
            orgs: [],
            countries: [],
            modules: []
        },
        // Filters, sort and page of the list, restored when returning from a request view
        clientRequestListState: null
    };
};

export const clientRequestState = defaultState();

export const clientRequestMutations = {
    [SET_ADMIN_CLIENT_REQUESTS](state, data) {
        state.clientRequests = data;
    },
    [ADD_ADMIN_CLIENT_REQUEST](state, clientRequest) {
        const list = state.clientRequests;

        if (!list.data) {
            return;
        }

        list.data.unshift(clientRequest);
        list.total++;
        list.last_page = Math.ceil(list.total / list.per_page);

        // Keep the page at its size; the last row moves to the next page
        if (list.data.length > list.per_page) {
            list.data.pop();
        }
    },
    [SET_ADMIN_CLIENT_REQUEST](state, clientRequest) {
        state.clientRequest = clientRequest;
        if (clientRequest && state.clientRequests.data) {
            const index = state.clientRequests.data.findIndex(r => r.id === clientRequest.id);
            if (index !== -1) {
                state.clientRequests.data.splice(index, 1, {
                    ...state.clientRequests.data[index],
                    ...clientRequest
                });
            }
        }
    },
    [REMOVE_ADMIN_CLIENT_REQUEST](state, id) {
        const list = state.clientRequests;

        if (list.data) {
            const index = list.data.findIndex(r => r.id === id);
            if (index !== -1) {
                list.data.splice(index, 1);
                list.total--;
                list.last_page = Math.max(Math.ceil(list.total / list.per_page), 1);
            }
        }
        if (state.clientRequest?.id === id) {
            state.clientRequest = null;
        }
    },
    [ADD_ADMIN_CLIENT_REQUEST_CLIENT](state, client) {
        if (!state.clientRequest) {
            return;
        }
        state.clientRequest = {
            ...state.clientRequest,
            clients: [...(state.clientRequest.clients || []), client]
        };
    },
    [SET_ADMIN_CLIENT_REQUEST_CLIENT](state, client) {
        if (!state.clientRequest?.clients) {
            return;
        }
        const index = state.clientRequest.clients.findIndex(c => c.id === client.id);
        if (index !== -1) {
            state.clientRequest.clients.splice(index, 1, client);
        }
    },
    [REMOVE_ADMIN_CLIENT_REQUEST_CLIENT](state, clientId) {
        if (!state.clientRequest?.clients) {
            return;
        }
        const index = state.clientRequest.clients.findIndex(c => c.id === clientId);
        if (index !== -1) {
            state.clientRequest.clients.splice(index, 1);
        }
    },
    [ADD_ADMIN_CLIENT_REQUEST_COMMENT](state, comment) {
        if (!state.clientRequest) {
            return;
        }
        state.clientRequest = {
            ...state.clientRequest,
            comments: [...(state.clientRequest.comments || []), comment]
        };
    },
    [REMOVE_ADMIN_CLIENT_REQUEST_COMMENT](state, commentId) {
        if (!state.clientRequest?.comments) {
            return;
        }
        const index = state.clientRequest.comments.findIndex(c => c.id === commentId);
        if (index !== -1) {
            state.clientRequest.comments.splice(index, 1);
        }
    },
    [SET_ADMIN_CLIENT_REQUEST_OPTIONS](state, options) {
        state.clientRequestOptions = options;
    },
    [SET_ADMIN_CLIENT_REQUEST_LIST_STATE](state, listState) {
        state.clientRequestListState = listState;
    },
    [ADD_ADMIN_CLIENT_REQUEST_FILE](state, file) {
        if (!state.clientRequest) {
            return;
        }
        state.clientRequest = {
            ...state.clientRequest,
            files: [...(state.clientRequest.files || []), file]
        };
    },
    [REMOVE_ADMIN_CLIENT_REQUEST_FILE](state, fileId) {
        if (!state.clientRequest?.files) {
            return;
        }
        const index = state.clientRequest.files.findIndex(f => f.id === fileId);
        if (index !== -1) {
            state.clientRequest.files.splice(index, 1);
        }
    }
};

export const clientRequestActions = {
    [GET_ADMIN_CLIENT_REQUESTS]({commit}, queryObj) {
        return AdminClientRequestService.getClientRequests(queryObj).then(res => {
            commit(SET_ADMIN_CLIENT_REQUESTS, res);
        });
    },
    [GET_ADMIN_CLIENT_REQUEST]({commit}, id) {
        return AdminClientRequestService.getClientRequest(id).then(res => {
            commit(SET_ADMIN_CLIENT_REQUEST, res);
            return res;
        });
    },
    [STORE_ADMIN_CLIENT_REQUEST]({commit}, data) {
        return AdminClientRequestService.addClientRequest(data).then(res => {
            commit(ADD_ADMIN_CLIENT_REQUEST, res);
            return res;
        });
    },
    [UPDATE_ADMIN_CLIENT_REQUEST]({commit}, {id, data}) {
        return AdminClientRequestService.updateClientRequest(id, data).then(res => {
            commit(SET_ADMIN_CLIENT_REQUEST, res);
            return res;
        });
    },
    [UPDATE_ADMIN_CLIENT_REQUEST_STATUS]({commit}, {id, data}) {
        return AdminClientRequestService.updateStatus(id, data).then(res => {
            commit(SET_ADMIN_CLIENT_REQUEST, res);
            return res;
        });
    },
    [DELETE_ADMIN_CLIENT_REQUEST]({commit}, id) {
        return AdminClientRequestService.deleteClientRequest(id).then(() => {
            commit(REMOVE_ADMIN_CLIENT_REQUEST, id);
        });
    },
    [STORE_ADMIN_CLIENT_REQUEST_CLIENT]({commit}, {id, data}) {
        return AdminClientRequestService.addClient(id, data).then(res => {
            commit(ADD_ADMIN_CLIENT_REQUEST_CLIENT, res);
            return res;
        });
    },
    [UPDATE_ADMIN_CLIENT_REQUEST_CLIENT]({commit}, {id, clientId, data}) {
        return AdminClientRequestService.updateClient(id, clientId, data).then(res => {
            commit(SET_ADMIN_CLIENT_REQUEST_CLIENT, res);
            return res;
        });
    },
    [DELETE_ADMIN_CLIENT_REQUEST_CLIENT]({commit}, {id, clientId}) {
        return AdminClientRequestService.deleteClient(id, clientId).then(() => {
            commit(REMOVE_ADMIN_CLIENT_REQUEST_CLIENT, clientId);
        });
    },
    [STORE_ADMIN_CLIENT_REQUEST_COMMENT]({commit}, {id, data}) {
        return AdminClientRequestService.addComment(id, data).then(res => {
            commit(ADD_ADMIN_CLIENT_REQUEST_COMMENT, res);
            return res;
        });
    },
    [DELETE_ADMIN_CLIENT_REQUEST_COMMENT]({commit}, {id, commentId}) {
        return AdminClientRequestService.deleteComment(id, commentId).then(() => {
            commit(REMOVE_ADMIN_CLIENT_REQUEST_COMMENT, commentId);
        });
    },
    [GET_ADMIN_CLIENT_REQUEST_OPTIONS]({commit}) {
        return AdminClientRequestService.getOptions().then(res => {
            commit(SET_ADMIN_CLIENT_REQUEST_OPTIONS, res);
            return res;
        });
    },
    [STORE_ADMIN_CLIENT_REQUEST_FILE]({commit}, {id, formData}) {
        return AdminClientRequestService.uploadFile(id, formData).then(res => {
            commit(ADD_ADMIN_CLIENT_REQUEST_FILE, res);
            return res;
        });
    },
    [DELETE_ADMIN_CLIENT_REQUEST_FILE]({commit}, {id, fileId}) {
        return AdminClientRequestService.deleteFile(id, fileId).then(() => {
            commit(REMOVE_ADMIN_CLIENT_REQUEST_FILE, fileId);
        });
    }
};

export const clientRequestGetters = {
    adminClientRequests: state => state.clientRequests,
    adminClientRequest: state => state.clientRequest,
    adminClientRequestOptions: state => state.clientRequestOptions,
    adminClientRequestListState: state => state.clientRequestListState
};
